import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { 
  Calendar, 
  Users, 
  MapPin, 
  ChevronRight, 
  Plus, 
  Trophy,
  Loader2,
  FileText,
  Clock,
  BarChart3,
  Trash2,
  Copy,
  CheckSquare,
  Square,
  X,
  Edit3,
  Check,
  Archive,
  ArchiveRestore,
  Sparkles,
  ShieldCheck
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { GoProBanner } from '@/components/GoProBanner';
import { ConfirmDeleteDialog } from '@/components/ConfirmDeleteDialog';
import { RecentlyDeletedDialog } from '@/components/RecentlyDeletedDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MySchedulesPage() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [deleteScheduleTarget, setDeleteScheduleTarget] = useState(null);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [showTrash, setShowTrash] = useState(false);
  const [duplicating, setDuplicating] = useState(null);
  const [duplicateName, setDuplicateName] = useState('');
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [archivedSchedules, setArchivedSchedules] = useState([]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }
    if (user) {
      fetchSchedules();
    }
  }, [user, authLoading, navigate]);

  const fetchSchedules = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API_URL}/api/events/my-schedules`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSchedules(data.schedules || []);
      } else {
        toast.error('Failed to load schedules');
      }
    } catch (error) {
      console.error('Error fetching schedules:', error);
      toast.error('Failed to load schedules');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSchedule = (e, scheduleId, scheduleName) => {
    e.stopPropagation(); // Prevent card click navigation
    setDeleteScheduleTarget({ id: scheduleId, name: scheduleName });
  };

  const confirmDeleteSchedule = async () => {
    const { id: scheduleId } = deleteScheduleTarget;
    setDeletingId(scheduleId);
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API_URL}/api/events/${scheduleId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setSchedules(prev => prev.filter(s => s.id !== scheduleId));
        setDeleteScheduleTarget(null);
        toast.success('Schedule moved to Recently deleted');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to delete schedule');
      }
    } catch (error) {
      console.error('Error deleting schedule:', error);
      toast.error('Failed to delete schedule');
    } finally {
      setDeletingId(null);
    }
  };

  const handleArchiveSchedule = async (e, scheduleId, scheduleName) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API_URL}/api/events/${scheduleId}/archive`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setSchedules(prev => prev.filter(s => s.id !== scheduleId));
        toast.success(`"${scheduleName}" archived`);
      }
    } catch { toast.error('Failed to archive'); }
  };

  const handleUnarchive = async (scheduleId, scheduleName) => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API_URL}/api/events/${scheduleId}/unarchive`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setArchivedSchedules(prev => prev.filter(s => s.id !== scheduleId));
        toast.success(`"${scheduleName}" restored`);
        fetchSchedules();
      }
    } catch { toast.error('Failed to restore'); }
  };

  const fetchArchived = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API_URL}/api/events/archived`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setArchivedSchedules(data.schedules || []);
      }
    } catch { }
  };

  // Bulk actions
  const toggleSelect = (scheduleId) => {
    setSelectedIds(prev => 
      prev.includes(scheduleId) 
        ? prev.filter(id => id !== scheduleId)
        : [...prev, scheduleId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === schedules.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(schedules.map(s => s.id));
    }
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) return;
    setBulkConfirmOpen(true);
  };

  const confirmBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API_URL}/api/events/bulk-delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ event_ids: selectedIds })
      });

      const data = await response.json();
      
      if (response.ok) {
        setSchedules(prev => prev.filter(s => !selectedIds.includes(s.id)));
        setSelectedIds([]);
        setBulkMode(false);
        setBulkConfirmOpen(false);
        toast.success(data.message || `Deleted ${data.deleted_count} schedule(s)`);
      } else {
        toast.error(data.detail || 'Failed to delete schedules');
      }
    } catch (error) {
      console.error('Error bulk deleting:', error);
      toast.error('Failed to delete schedules');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleDuplicate = async (schedule) => {
    if (!duplicateName.trim()) {
      toast.error('Please enter a name for the duplicated schedule');
      return;
    }

    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API_URL}/api/events/duplicate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          event_id: schedule.id,
          new_name: duplicateName.trim()
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        // Add new schedule to list
        setSchedules(prev => [
          {
            id: data.event.id,
            name: data.event.name,
            format: data.event.format,
            start_date: data.event.start_date,
            team_count: data.event.teams?.length || 0,
            venue: data.event.venue,
            created_at: data.event.created_at
          },
          ...prev
        ]);
        setDuplicating(null);
        setDuplicateName('');
        toast.success(data.message);
      } else {
        toast.error(data.detail || 'Failed to duplicate schedule');
      }
    } catch (error) {
      console.error('Error duplicating:', error);
      toast.error('Failed to duplicate schedule');
    }
  };

  const cancelBulkMode = () => {
    setBulkMode(false);
    setSelectedIds([]);
  };

  const handleRename = async (scheduleId) => {
    if (!renameValue.trim()) {
      toast.error('Please enter a schedule name');
      return;
    }
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API_URL}/api/events/${scheduleId}/name`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name: renameValue.trim() })
      });
      const data = await response.json();
      if (response.ok) {
        setSchedules(prev => prev.map(s => s.id === scheduleId ? { ...s, name: data.name } : s));
        toast.success('Schedule renamed');
      } else {
        toast.error(data.detail || 'Failed to rename schedule');
      }
    } catch (error) {
      console.error('Error renaming:', error);
      toast.error('Failed to rename schedule');
    } finally {
      setRenamingId(null);
      setRenameValue('');
    }
  };

  const getFormatLabel = (format) => {
    const formats = {
      'round_robin': 'Round Robin',
      'double_round_robin': 'Double RR',
      'single_elimination': 'Single Elim',
      'double_elimination': 'Double Elim',
      'hybrid': 'Hybrid'
    };
    return formats[format] || format;
  };

  const getFormatColor = (format) => {
    const colors = {
      'round_robin': 'bg-blue-100 text-blue-700',
      'double_round_robin': 'bg-indigo-100 text-indigo-700',
      'single_elimination': 'bg-amber-100 text-amber-700',
      'double_elimination': 'bg-red-100 text-red-700',
      'hybrid': 'bg-purple-100 text-purple-700'
    };
    return colors[format] || 'bg-slate-100 text-slate-700';
  };

  const isPro = user?.subscription?.startsWith('pro_');

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00247D]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-transparent.png" alt="Schedule Guru" className="h-10" />
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/officials" className="hidden md:block">
                <Button variant="ghost" className="text-[#00247D]" data-testid="header-officials-btn">
                  <ShieldCheck className="w-4 h-4 mr-2" />
                  Officials
                </Button>
              </Link>
              <Link to="/import">
                <Button variant="outline" className="border-[#00247D] text-[#00247D] hover:bg-[#00247D]/5" data-testid="import-pdf-btn">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Import Schedule
                </Button>
              </Link>
              <Link to="/create">
                <Button className="bg-[#00247D] hover:bg-[#001A5C]" data-testid="create-new-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create New
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>
      <GoProBanner />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>
            MY SCHEDULES
          </h1>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
            <p className="text-slate-600 w-full sm:w-auto">
              View and manage your tournament schedules
            </p>
            {isPro && (
              <button
                className={`text-sm font-medium flex items-center gap-1 whitespace-nowrap transition-colors ${showArchived ? 'text-[#00247D]' : 'text-slate-400 hover:text-slate-600'}`}
                onClick={() => { setShowArchived(!showArchived); if (!showArchived) fetchArchived(); }}
                data-testid="toggle-archived-btn">
                <Archive className="w-3.5 h-3.5" />
                {showArchived ? 'Hide Archived' : 'View Archived'}
              </button>
            )}
            <button
              className="text-sm font-medium flex items-center gap-1 whitespace-nowrap text-slate-400 hover:text-slate-600 transition-colors"
              onClick={() => setShowTrash(true)}
              data-testid="recently-deleted-btn">
              <Trash2 className="w-3.5 h-3.5" />
              Recently deleted
            </button>
          </div>
        </div>

        {/* Pro Badge */}
        {isPro ? (
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge className="bg-gradient-to-r from-amber-400 to-amber-500 text-white">
                <Trophy className="w-3 h-3 mr-1" />
                PRO
              </Badge>
              <span className="text-sm text-slate-500">Bulk actions, score entry, live standings & more</span>
            </div>
            {schedules.length > 0 && !bulkMode && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setBulkMode(true)}
                data-testid="bulk-select-btn"
              >
                <CheckSquare className="w-4 h-4 mr-2" />
                Select
              </Button>
            )}
          </div>
        ) : (
          <Card className="mb-6 border-amber-200 bg-amber-50">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <Trophy className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">Upgrade to Pro</p>
                    <p className="text-sm text-slate-600">Access previously created schedules, update scores and view live standings</p>
                  </div>
                </div>
                <Link to="/pricing">
                  <Button className="bg-[#00247D] hover:bg-[#001A5C]" data-testid="upgrade-cta-btn">
                    Upgrade Now
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Bulk Action Toolbar */}
        {bulkMode && isPro && (
          <div className="mb-6 p-4 bg-slate-100 rounded-lg border border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleSelectAll}
                className="text-slate-600"
                data-testid="select-all-btn"
              >
                {selectedIds.length === schedules.length ? (
                  <CheckSquare className="w-4 h-4 mr-2" />
                ) : (
                  <Square className="w-4 h-4 mr-2" />
                )}
                {selectedIds.length === schedules.length ? 'Deselect All' : 'Select All'}
              </Button>
              <span className="text-sm text-slate-500">
                {selectedIds.length} of {schedules.length} selected
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="destructive"
                size="sm"
                onClick={handleBulkDelete}
                disabled={selectedIds.length === 0 || bulkDeleting}
                data-testid="bulk-delete-btn"
              >
                {bulkDeleting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Delete Selected
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={cancelBulkMode}
                data-testid="cancel-bulk-btn"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {schedules.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="py-16 text-center">
              <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No schedules yet</h3>
              <p className="text-slate-500 mb-6">Create your first tournament schedule to get started</p>
              <Link to="/create">
                <Button className="bg-[#00247D] hover:bg-[#001A5C]">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Schedule
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {schedules.map((schedule) => (
              <Card 
                key={schedule.id} 
                className={`border-slate-200 hover:shadow-lg transition-all cursor-pointer group relative ${
                  selectedIds.includes(schedule.id) ? 'ring-2 ring-[#00247D] bg-blue-50/50' : ''
                }`}
                onClick={(e) => {
                  if (bulkMode) {
                    e.preventDefault();
                    toggleSelect(schedule.id);
                  } else {
                    navigate(`/schedule/${schedule.id}`);
                  }
                }}
                data-testid={`schedule-card-${schedule.id}`}
              >
                {/* Checkbox for bulk mode */}
                {bulkMode && isPro && (
                  <div className="absolute top-3 left-3 z-10">
                    <Checkbox
                      checked={selectedIds.includes(schedule.id)}
                      onCheckedChange={() => toggleSelect(schedule.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="h-5 w-5 border-2"
                      data-testid={`select-checkbox-${schedule.id}`}
                    />
                  </div>
                )}
                <CardHeader className={`pb-2 ${bulkMode ? 'pl-10' : ''}`}>
                  <div className="flex items-start justify-between">
                    {renamingId === schedule.id ? (
                      <div className="flex items-center gap-1 flex-1 pr-2" onClick={(e) => e.stopPropagation()}>
                        <Input
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          className="h-8 text-sm font-bold"
                          autoFocus
                          data-testid={`rename-input-${schedule.id}`}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleRename(schedule.id);
                            if (e.key === 'Escape') { setRenamingId(null); setRenameValue(''); }
                          }}
                        />
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-green-600 hover:text-green-700 hover:bg-green-50"
                          onClick={(e) => { e.stopPropagation(); handleRename(schedule.id); }}
                          data-testid={`confirm-rename-${schedule.id}`}
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-slate-400 hover:text-slate-600"
                          onClick={(e) => { e.stopPropagation(); setRenamingId(null); setRenameValue(''); }}
                          data-testid={`cancel-rename-${schedule.id}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <CardTitle className="text-lg font-bold text-slate-800 group-hover:text-[#00247D] transition-colors pr-2">
                        {schedule.name}
                      </CardTitle>
                    )}
                    {!bulkMode && renamingId !== schedule.id && (
                      <div className="flex items-center gap-1">
                        {/* Rename button (Pro only) */}
                        {isPro && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-400 hover:text-[#00247D] hover:bg-blue-50 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => {
                              e.stopPropagation();
                              setRenamingId(schedule.id);
                              setRenameValue(schedule.name);
                            }}
                            data-testid={`rename-schedule-${schedule.id}`}
                            title="Rename schedule"
                          >
                            <Edit3 className="w-4 h-4" />
                          </Button>
                        )}
                        {/* Duplicate button (Pro only) */}
                        {isPro && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-400 hover:text-[#00247D] hover:bg-blue-50 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDuplicating(schedule);
                              setDuplicateName(`${schedule.name} (Copy)`);
                            }}
                            data-testid={`duplicate-schedule-${schedule.id}`}
                            title="Duplicate schedule"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        )}
                        {/* Archive button (Pro only). Always visible on
                            mobile so users can discover it without hovering;
                            keeps the hover-fade behaviour on desktop to stay
                            consistent with the other row actions. */}
                        {isPro && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-400 hover:text-amber-600 hover:bg-amber-50 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                            onClick={(e) => handleArchiveSchedule(e, schedule.id, schedule.name)}
                            data-testid={`archive-schedule-${schedule.id}`}
                            title="Archive schedule"
                            aria-label="Archive schedule"
                          >
                            <Archive className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-slate-400 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => handleDeleteSchedule(e, schedule.id, schedule.name)}
                          disabled={deletingId === schedule.id}
                          data-testid={`delete-schedule-${schedule.id}`}
                          title="Delete schedule"
                        >
                          {deletingId === schedule.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                        <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-[#00247D] transition-colors" />
                      </div>
                    )}
                  </div>
                  <Badge className={`w-fit ${getFormatColor(schedule.format)}`}>
                    {getFormatLabel(schedule.format)}
                  </Badge>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      <span>
                        {schedule.start_date ? format(parseISO(schedule.start_date), 'MMM d, yyyy') : 'No date set'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-slate-400" />
                      <span>{schedule.team_count} teams</span>
                    </div>
                    {schedule.venue && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-slate-400" />
                        <span className="truncate">{schedule.venue}</span>
                      </div>
                    )}
                    {schedule.created_at && (
                      <div className="flex items-center gap-2 text-xs text-slate-400">
                        <Clock className="w-3 h-3" />
                        <span>Created {format(parseISO(schedule.created_at), 'MMM d, yyyy')}</span>
                      </div>
                    )}
                  </div>

                  {/* Pro Features CTA */}
                  {isPro && (
                    <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-[#00247D]" />
                      <span className="text-xs text-[#00247D] font-medium">
                        Enter scores & view standings
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Quick Actions */}
        <div className="mt-8 pt-8 border-t border-slate-200">
          <h2 className="text-lg font-semibold text-slate-700 mb-4">Quick Actions</h2>
          <div className="flex flex-wrap gap-3">
            <Link to="/create">
              <Button variant="outline" className="border-[#00247D] text-[#00247D] hover:bg-[#00247D] hover:text-white">
                <Plus className="w-4 h-4 mr-2" />
                New Schedule
              </Button>
            </Link>
            <Link to="/pricing">
              <Button variant="outline">
                <Trophy className="w-4 h-4 mr-2" />
                {isPro ? 'Manage Subscription' : 'Upgrade to Pro'}
              </Button>
            </Link>
          </div>
        </div>
        {/* Archived Schedules Section — Pro only */}
        {isPro && showArchived && (
          <div className="mt-8 border-t border-slate-200 pt-6">
            <h2 className="text-lg font-bold text-slate-600 mb-4 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
              <Archive className="w-4 h-4" /> ARCHIVED SCHEDULES
            </h2>
            {archivedSchedules.length === 0 ? (
              <p className="text-sm text-slate-400">No archived schedules</p>
            ) : (
              <div className="space-y-2">
                {archivedSchedules.map(s => (
                  <div key={s.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid={`archived-${s.id}`}>
                    <div>
                      <span className="font-medium text-slate-700">{s.name}</span>
                      <span className="text-xs text-slate-400 ml-3">{s.team_count} teams &middot; {s.format}</span>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => handleUnarchive(s.id, s.name)} data-testid={`unarchive-${s.id}`}>
                      <ArchiveRestore className="w-3.5 h-3.5 mr-1" /> Restore
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Duplicate Modal */}
      {duplicating && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4 border-slate-200 shadow-xl">
            <CardHeader>
              <CardTitle className="text-xl font-bold text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>
                Duplicate Schedule
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-600">
                Create a copy of "{duplicating.name}" with a new name. All match data will be copied, but scores will be reset.
              </p>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  New Schedule Name
                </label>
                <Input
                  value={duplicateName}
                  onChange={(e) => setDuplicateName(e.target.value)}
                  placeholder="Enter name for duplicated schedule"
                  data-testid="duplicate-name-input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleDuplicate(duplicating);
                    }
                  }}
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setDuplicating(null);
                    setDuplicateName('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-[#00247D] hover:bg-[#001A5C]"
                  onClick={() => handleDuplicate(duplicating)}
                  disabled={!duplicateName.trim()}
                  data-testid="confirm-duplicate-btn"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Duplicate
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      <ConfirmDeleteDialog
        open={!!deleteScheduleTarget}
        title="Delete schedule?"
        itemName={deleteScheduleTarget?.name}
        description="will move to Recently deleted. You can restore it within 7 days."
        confirmLabel="Delete Schedule"
        onClose={() => setDeleteScheduleTarget(null)}
        onConfirm={confirmDeleteSchedule}
        testId="delete-schedule"
      />
      <ConfirmDeleteDialog
        open={bulkConfirmOpen}
        title={`Delete ${selectedIds.length} schedule${selectedIds.length === 1 ? '' : 's'}?`}
        description={`The ${selectedIds.length} selected schedule${selectedIds.length === 1 ? '' : 's'} will move to Recently deleted. You can restore them within 7 days.`}
        confirmLabel="Delete Schedules"
        onClose={() => setBulkConfirmOpen(false)}
        onConfirm={confirmBulkDelete}
        testId="bulk-delete"
      />
      <RecentlyDeletedDialog open={showTrash} onClose={() => setShowTrash(false)} group="events" onRestored={() => fetchSchedules()} />
    </div>
  );
}
